<template>
  <div class="pct-tier-view">
    <!-- Header Bar -->
    <div class="tier-header">
      <div class="header-left">
        <div class="header-title" v-if="data">
          <span class="date-range">{{ data.start_date }} 至 {{ data.date1 }}</span>
          <span class="title-badge">{{ data.days }}天涨幅梯队</span>
        </div>
      </div>
      <div class="header-right">
        <!-- Days Toggle -->
        <div class="days-toggle-pill">
          <button
            v-for="d in [10, 20, 30]"
            :key="d"
            class="days-btn"
            :class="{ active: selectedDays === d }"
            @click="onDaysChange(d)"
          >{{ d }}天</button>
        </div>
        <!-- Date Picker -->
        <b-form-datepicker
          v-model="selectedDate"
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
          size="sm"
          class="date-picker"
          placeholder="选择日期"
          locale="zh"
          hide-header
          button-only
          button-variant="outline-secondary"
          @input="onDateChange"
        ></b-form-datepicker>
        <span class="selected-date-label" v-if="selectedDate">{{ selectedDate }}</span>
        <WxShare :title="shareTitle" :desc="shareDesc" :link="shareLink" class="ml-2" />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <b-spinner small variant="primary"></b-spinner>
      <span>数据加载中...</span>
    </div>

    <!-- Empty -->
    <div v-else-if="!data || (!data.up_tiers && !data.down_tiers)" class="empty-state">
      <div class="empty-icon">📊</div>
      <div>暂无涨幅梯队数据</div>
      <div class="empty-hint">请先执行数据回填脚本或等待定时任务在 16:00/20:00 自动计算</div>
    </div>

    <!-- Main Content -->
    <div v-else class="tier-content">
      <!-- 上涨梯队 -->
      <div class="tier-section">
        <div class="section-header up">
          <span class="section-icon">▲</span>
          上涨梯队
          <span class="section-count">共 {{ upTotalCount }} 只</span>
          <!-- Group Toggle Switch -->
          <label class="group-switch-label ml-auto">
            <span class="group-switch-text">分组</span>
            <span class="group-switch-track" :class="{ on: groupEnabled }" @click.stop="groupEnabled = !groupEnabled">
              <span class="group-switch-thumb"></span>
            </span>
          </label>
        </div>
        <div class="tier-table">
          <div
            v-for="tier in data.up_tiers"
            :key="tier.range"
            class="tier-row"
          >
            <!-- Range Label -->
            <div class="range-col up">
              <span class="range-text">{{ tier.range }}</span>
              <span class="range-count">{{ tier.count }}</span>
            </div>
            <!-- Stock Grid -->
            <div class="stock-grid-grouped">
              <div
                v-if="tier.count === 0"
                class="no-stocks"
              >—</div>
              <template v-else-if="groupEnabled && tier.concept_groups && (tier.concept_groups.length > 1 || (tier.concept_groups.length === 1 && tier.concept_groups[0].concept !== '其他'))">
                <div 
                  v-for="group in tier.concept_groups" 
                  :key="group.concept" 
                  class="concept-group-box"
                >
                  <!-- Group Header + Stocks inline -->
                  <div class="concept-group-inline">
                    <span class="concept-group-header">{{ group.concept }}<span class="group-count-badge">{{ group.count }}</span></span>
                    <div class="concept-group-stocks">
                      <div
                        v-for="stock in group.stocks"
                        :key="stock.stock_code"
                        class="stock-cell up"
                        v-b-toggle.sidebar-right-stock
                        @click="handleStockClick(stock)"
                      >
                        <div class="cell-top">
                          <span class="cell-market">{{ stock.market_type }}</span>
                          <span class="cell-name">{{ stock.stock_name }}</span>
                          <span class="cell-pct up-pct">+{{ stock.pct.toFixed(1) }}%</span>
                        </div>
                        <div class="cell-bottom">
                          <span v-if="stock.concept" class="cell-concept">{{ stock.concept }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <!-- Fallback to original flat list -->
                <div class="concept-group-stocks">
                  <div
                    v-for="stock in tier.stocks"
                    :key="stock.stock_code"
                    class="stock-cell up"
                    v-b-toggle.sidebar-right-stock
                    @click="handleStockClick(stock)"
                  >
                    <div class="cell-top">
                      <span class="cell-market">{{ stock.market_type }}</span>
                      <span class="cell-name">{{ stock.stock_name }}</span>
                      <span class="cell-pct up-pct">+{{ stock.pct.toFixed(1) }}%</span>
                    </div>
                    <div class="cell-bottom">
                      <span v-if="stock.concept" class="cell-concept">{{ stock.concept }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 下跌梯队 -->
      <div class="tier-section mt-3">
        <div class="section-header down">
          <span class="section-icon">▼</span>
          下跌梯队
          <span class="section-count">共 {{ downTotalCount }} 只</span>
        </div>
        <div class="tier-table">
          <div
            v-for="tier in data.down_tiers"
            :key="tier.range"
            class="tier-row"
          >
            <!-- Range Label -->
            <div class="range-col down">
              <span class="range-text">{{ tier.range }}</span>
              <span class="range-count">{{ tier.count }}</span>
            </div>
            <!-- Stock Grid -->
            <div class="stock-grid-grouped">
              <div
                v-if="tier.count === 0"
                class="no-stocks"
              >—</div>
              <template v-else-if="groupEnabled && tier.concept_groups && (tier.concept_groups.length > 1 || (tier.concept_groups.length === 1 && tier.concept_groups[0].concept !== '其他'))">
                <div 
                  v-for="group in tier.concept_groups" 
                  :key="group.concept" 
                  class="concept-group-box"
                >
                  <!-- Group Header + Stocks inline -->
                  <div class="concept-group-inline">
                    <span class="concept-group-header">{{ group.concept }}<span class="group-count-badge">{{ group.count }}</span></span>
                    <div class="concept-group-stocks">
                      <div
                        v-for="stock in group.stocks"
                        :key="stock.stock_code"
                        class="stock-cell down"
                        v-b-toggle.sidebar-right-stock
                        @click="handleStockClick(stock)"
                      >
                        <div class="cell-top">
                          <span class="cell-market">{{ stock.market_type }}</span>
                          <span class="cell-name">{{ stock.stock_name }}</span>
                          <span class="cell-pct down-pct">{{ stock.pct.toFixed(1) }}%</span>
                        </div>
                        <div class="cell-bottom">
                          <span v-if="stock.concept" class="cell-concept">{{ stock.concept }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <!-- Fallback to original flat list -->
                <div class="concept-group-stocks">
                  <div
                    v-for="stock in tier.stocks"
                    :key="stock.stock_code"
                    class="stock-cell down"
                    v-b-toggle.sidebar-right-stock
                    @click="handleStockClick(stock)"
                  >
                    <div class="cell-top">
                      <span class="cell-market">{{ stock.market_type }}</span>
                      <span class="cell-name">{{ stock.stock_name }}</span>
                      <span class="cell-pct down-pct">{{ stock.pct.toFixed(1) }}%</span>
                    </div>
                    <div class="cell-bottom">
                      <span v-if="stock.concept" class="cell-concept">{{ stock.concept }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import WxShare from '@/components/share/WxShare.vue'

export default {
  name: 'PctTierView',
  components: {
    WxShare
  },
  data () {
    return {
      loading: false,
      selectedDays: 10,
      selectedDate: '',
      data: null,
      groupEnabled: false
    }
  },
  computed: {
    upTotalCount () {
      if (!this.data || !this.data.up_tiers) return 0
      return this.data.up_tiers.reduce((sum, t) => sum + t.count, 0)
    },
    downTotalCount () {
      if (!this.data || !this.data.down_tiers) return 0
      return this.data.down_tiers.reduce((sum, t) => sum + t.count, 0)
    },
    shareTitle () {
      const dateStr = this.data ? this.data.date1 : (this.selectedDate || '')
      return `${this.selectedDays}天涨幅梯队 (${dateStr}) - 自在量化`
    },
    shareDesc () {
      return '全市场个股区间累计涨幅梯队分布'
    },
    shareLink () {
      const origin = window.location.origin
      const dateQuery = this.selectedDate ? `&date1=${this.selectedDate}` : ''
      return `${origin}/review/uplimit/tier?days=${this.selectedDays}${dateQuery}`
    }
  },
  mounted () {
    if (this.$route.query.days) {
      const d = parseInt(this.$route.query.days, 10)
      if ([10, 20, 30].includes(d)) {
        this.selectedDays = d
      }
    }
    if (this.$route.query.date1) {
      this.selectedDate = this.$route.query.date1
    }
    this.fetchData()
  },
  methods: {
    onDaysChange (days) {
      this.selectedDays = days
      this.fetchData()
    },
    onDateChange () {
      this.fetchData()
    },
    fetchData () {
      this.loading = true
      const params = { days: this.selectedDays }
      if (this.selectedDate) {
        params.date1 = this.selectedDate
      }
      request_zzquant({
        url: '/v3/market/pct-tier',
        params
      })
        .then(res => {
          const resData = res && res.data ? res.data : {}
          if (resData.code === 200 && resData.data) {
            const data = resData.data
            if (data.down_tiers) {
              // 1. 跌幅大的区间排在前面（即负值越小的排在越前）
              const parseRangeValue = (rangeStr) => {
                const matches = rangeStr.match(/-?\d+/g)
                if (matches && matches.length > 0) {
                  return parseInt(matches[0], 10)
                }
                return 0
              }
              data.down_tiers.sort((a, b) => parseRangeValue(a.range) - parseRangeValue(b.range))

              // 2. 后端已经对个股进行了概念分箱以及涨幅/跌幅的精确排序，无需在前端重排覆盖
              // data.down_tiers.forEach(tier => {
              //   if (tier.stocks) {
              //     tier.stocks.sort((a, b) => a.pct - b.pct)
              //   }
              // })
            }
            this.data = data
          } else {
            this.data = null
          }
        })
        .catch(err => {
          console.error('PctTierView fetch error:', err)
          this.data = null
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleStockClick (stock) {
      this.$emit('show_sidebar', {
        stock_code: stock.stock_code,
        stock_name: stock.stock_name,
        date1: this.data ? this.data.date1 : ''
      })
    }
  }
}
</script>

<style scoped>
.pct-tier-view {
  padding: 0 0 16px 0;
}

/* ---- Header ---- */
.tier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 4px 12px 4px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-range {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-main);
}

.title-badge {
  font-size: 11px;
  background: rgba(13, 110, 253, 0.12);
  color: var(--accent-color);
  border-radius: 4px;
  padding: 1px 7px;
  font-weight: 600;
}

.header-updated {
  font-size: 11px;
  color: var(--text-muted);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ---- Days Toggle ---- */
.days-toggle-pill {
  display: flex;
  background: var(--bg-muted);
  border: 1px solid var(--border-color);
  padding: 3px;
  border-radius: 999px;
  gap: 2px;
}

.days-btn {
  border: none;
  background: transparent;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.days-btn:hover {
  color: var(--text-main);
}

.days-btn.active {
  background: var(--bg-card);
  color: var(--accent-color);
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.selected-date-label {
  font-size: 12px;
  color: var(--text-muted);
}

/* ---- Loading / Empty ---- */
.loading-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px;
  justify-content: center;
  color: var(--text-muted);
  font-size: 13px;
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
  color: var(--text-muted);
  font-size: 13px;
}

.empty-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.empty-hint {
  font-size: 11px;
  margin-top: 6px;
  opacity: 0.7;
}

/* ---- Section ---- */
.tier-section {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-card);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.section-header.up {
  background: rgba(220, 53, 69, 0.07);
  color: #dc3545;
  border-bottom: 1px solid rgba(220, 53, 69, 0.15);
}

.section-header.down {
  background: rgba(25, 135, 84, 0.07);
  color: #198754;
  border-bottom: 1px solid rgba(25, 135, 84, 0.15);
}

body.theme-dark .section-header.up {
  background: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
}

body.theme-dark .section-header.down {
  background: rgba(72, 199, 142, 0.1);
  color: #48c78e;
}

.section-icon { font-size: 10px; }

.section-count {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.75;
}

/* ---- Table Layout ---- */
.tier-table {
  display: flex;
  flex-direction: column;
}

.tier-row {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  min-height: 42px;
}

.tier-row:last-child {
  border-bottom: none;
}

/* Range Label */
.range-col {
  width: 68px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 4px;
  border-right: 1px solid var(--border-color);
  gap: 1px;
}

.range-col.up { background: rgba(220, 53, 69, 0.05); }
.range-col.down { background: rgba(25, 135, 84, 0.05); }

body.theme-dark .range-col.up { background: rgba(255, 107, 107, 0.06); }
body.theme-dark .range-col.down { background: rgba(72, 199, 142, 0.06); }

.range-text {
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
}

.range-col.up .range-text { color: #dc3545; }
.range-col.down .range-text { color: #198754; }

body.theme-dark .range-col.up .range-text { color: #ff6b6b; }
body.theme-dark .range-col.down .range-text { color: #48c78e; }

.range-count {
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
}

.range-col.up .range-count { color: #dc3545; }
.range-col.down .range-count { color: #198754; }

body.theme-dark .range-col.up .range-count { color: #ff6b6b; }
body.theme-dark .range-col.down .range-count { color: #48c78e; }

/* ---- Stock Grid ---- */
.stock-grid {
  flex: 1;
  display: grid;
  /* 固定列宽，每格由 115px 调整为 124px，保证 4 个字名称完整显示 */
  grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
  gap: 4px;
  padding: 6px 8px;
  align-content: start;
}

.no-stocks {
  color: var(--text-muted);
  font-size: 12px;
  grid-column: 1 / -1;
  padding: 8px 0;
}

/* Stock Cell */
.stock-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 6px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  /* 固定最小宽度保证不会过窄 */
  min-width: 0;
}

.stock-cell.up {
  background: rgba(220, 53, 69, 0.06);
  border-color: rgba(220, 53, 69, 0.16);
}

.stock-cell.down {
  background: rgba(25, 135, 84, 0.06);
  border-color: rgba(25, 135, 84, 0.16);
}

.stock-cell.up:hover {
  background: rgba(220, 53, 69, 0.15);
  border-color: rgba(220, 53, 69, 0.35);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.12);
}

.stock-cell.down:hover {
  background: rgba(25, 135, 84, 0.15);
  border-color: rgba(25, 135, 84, 0.35);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(25, 135, 84, 0.12);
}

body.theme-dark .stock-cell.up {
  background: rgba(255, 107, 107, 0.08);
  border-color: rgba(255, 107, 107, 0.2);
}

body.theme-dark .stock-cell.down {
  background: rgba(72, 199, 142, 0.08);
  border-color: rgba(72, 199, 142, 0.2);
}

/* Cell top row: market badge + name */
.cell-top {
  display: flex;
  align-items: center;
  gap: 3px;
  overflow: hidden;
}

.cell-market {
  font-size: 9px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 2px;
  padding: 0 3px;
  color: var(--text-muted);
  flex-shrink: 0;
  line-height: 14px;
}

body.theme-dark .cell-market {
  background: rgba(255, 255, 255, 0.08);
}

.cell-name {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-main);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* Cell bottom row: pct + concept */
.cell-bottom {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  min-height: 15px;
}

.cell-pct {
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.up-pct { color: #dc3545; }
.down-pct { color: #198754; }

body.theme-dark .up-pct { color: #ff6b6b; }
body.theme-dark .down-pct { color: #48c78e; }

.cell-concept {
  font-size: 9px;
  color: var(--text-muted);
  background: var(--bg-muted);
  border-radius: 2px;
  padding: 0 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
  .stock-grid {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 3px;
    padding: 4px 6px;
  }

  .range-col {
    width: 52px;
  }

  .range-count {
    font-size: 13px;
  }

  /* 缩小字体以在平板端显示更多内容 */
  .cell-name {
    font-size: 9.5px;
  }
  .cell-pct {
    font-size: 9.5px;
  }
  .cell-market {
    font-size: 8px;
    line-height: 12px;
    padding: 0 2px;
  }
  .cell-concept {
    font-size: 8px;
    padding: 0 2px;
  }
  .cell-bottom {
    min-height: 13px;
  }
}

@media (max-width: 480px) {
  .tier-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 6px 4px 8px 4px;
  }

  .stock-grid {
    grid-template-columns: repeat(auto-fill, minmax(98px, 1fr));
    gap: 2px;
    padding: 2px 4px;
  }

  .range-col {
    width: 46px;
    padding: 4px 2px;
  }

  .range-text {
    font-size: 8px;
  }

  .range-count {
    font-size: 12px;
  }

  /* 缩小字体以在手机端显示更多内容 */
  .date-range {
    font-size: 11px;
  }
  .title-badge {
    font-size: 10px;
    padding: 1px 4px;
  }
  .days-btn {
    padding: 3px 10px;
    font-size: 11px;
  }

  .cell-name {
    font-size: 8.5px;
  }
  .cell-pct {
    font-size: 8.5px;
  }
  .cell-market {
    font-size: 7.5px;
    line-height: 11px;
    padding: 0 1.5px;
  }
  .cell-concept {
    font-size: 7.5px;
    padding: 0 1.5px;
  }
  .cell-bottom {
    min-height: 12px;
  }

  .stock-grid-grouped {
    padding: 2px 4px;
    gap: 6px;
  }
}

/* ---- Group Toggle Switch ---- */
.group-switch-label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}

.group-switch-text {
  font-size: 11px;
  color: inherit;
  opacity: 0.8;
  font-weight: 500;
  white-space: nowrap;
}

.group-switch-track {
  position: relative;
  display: inline-block;
  width: 30px;
  height: 17px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 999px;
  transition: background 0.22s ease;
  cursor: pointer;
  flex-shrink: 0;
}

body.theme-dark .group-switch-track {
  background: rgba(255, 255, 255, 0.2);
}

.group-switch-track.on {
  background: rgba(255, 255, 255, 0.55);
}

.group-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.group-switch-track.on .group-switch-thumb {
  transform: translateX(13px);
  opacity: 1;
}

/* ---- Grouped Stock Grid ---- */
.stock-grid-grouped {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 8px;
  align-content: start;
}

.concept-group-box {
  width: 100%;
}

/* 分组名称 + 个股在同一行，不换行 */
.concept-group-inline {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
}

.concept-group-header {
  flex-shrink: 0;
  width: 72px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 4px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  color: var(--text-main);
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  align-self: flex-start;
  margin-top: 2px;
  box-sizing: border-box;
}

body.theme-dark .concept-group-header {
  background-color: rgba(255, 255, 255, 0.08);
}

.group-count-badge {
  font-size: 10px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  padding: 0 4px;
  line-height: 16px;
  color: var(--text-muted);
}

body.theme-dark .group-count-badge {
  background: rgba(255, 255, 255, 0.12);
}

.concept-group-stocks {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
  gap: 4px;
  min-width: 0;
}

@media (max-width: 480px) {
  .concept-group-inline {
    flex-direction: row;
    align-items: flex-start;
    gap: 4px;
  }

  .concept-group-header {
    width: 60px;
    font-size: 9px;
    padding: 2px 3px;
  }

  .concept-group-stocks {
    grid-template-columns: repeat(auto-fill, minmax(98px, 1fr));
    gap: 2px;
  }

  .group-switch-text {
    font-size: 11px;
  }

  .group-switch-track {
    width: 28px;
    height: 16px;
  }

  .group-switch-thumb {
    width: 12px;
    height: 12px;
  }

  .group-switch-track.on .group-switch-thumb {
    transform: translateX(12px);
  }
}
</style>
